import SearchIcon from '@material-ui/icons/Search';
import PencilIcon from '@material-ui/icons/Create';
import CloseIcon from '@material-ui/icons/Close';

import clsx from "clsx";
import { Box, Button, Dialog, DialogContent, IconButton, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Paper, InputBase, Select, MenuItem } from "@material-ui/core";
import { forwardRef, useEffect, useRef, useState, useImperativeHandle, Suspense, useCallback, createRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { memo } from 'react';
import { Controller, useForm } from "react-hook-form";
import { coPilotListFilterWordState, useCoPilotList } from "../../store/CoPilotSettingState";
import { aircraftListFilterWordState } from "../../store/AircraftSettingState";
import { Loading } from "../../parts/Loading";
import { useAlert } from "../../hooks/useAlert";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { userState } from '../../store/UserState';
import RoleControl from '../RoleControl';
import { SUBJ_COPI_SETTING_LIST, VERB_UPDATE } from '../../utils/AccessControl';
import { strSJISBytes } from '../../utils/FPEUtils';

const useStyles = makeStyles({
	container: {
		height: '100%',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	tableCell: {
		padding: "6px 18px 6px 12px",
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		'&:nth-child(2)': {
			position: 'sticky',
			left: '34px',
			zIndex: 2,
		},
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+2)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconCell: {
		width: 32,
		minWidth: 32,
		maxWidth: 32,
		padding: 0,
	},
	sbdImeiCell: {
		width: 100,
		minWidth: 100,
	},
	aircraftCodeCell: {
		width: 100,
		minWidth: 100,
	},
	iridiumUpdateIntervalCell: {
		width: 100,
		minWidth: 100,
	},
	remarksCell: {
		wordBreak: 'break-all',
	},
	operatingCompanyCell: {
		width: 100,
		minWidth: 100,
		whiteSpace: 'nowrap',
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		// padding: '0 16px',
		// height: '100%',
		// position: 'absolute',
		// pointerEvents: 'none',
		// display: 'flex',
		// alignItems: 'center',
		// justifyContent: 'center',
		color: 'rgba(0, 0, 0, 0.54)',
	},
	intervalSecSelect: {
		minWidth: 60,
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		}
	},
	remarksInputText: {
		width: 400,
	},
	dialogTitle: {
		fontWeight: "bold",
		paddingBottom: 15,
	},
	dialogContent: {
		'& li': {
			marginTop: 2,
			marginBottom: 2,
		},
	},
	note: {
		marginBottom: '20px !important',
		color: '#ff0000',
	},
	dataList: {
		display: 'flex',
		flexWrap: 'nowrap',
		'& dd:not(:first-child)': {
			marginLeft: 5,
		},
	},
	dataLabel: {
		display: 'flex',
		alignItems: 'center',
		// width: 200,
		height: 38,
		marginRight: 15,
	},
});

const columns = [
	{ id: 'sbd_imei', label: 'SBD IMEI', classes: ['sbdImeiCell'] },
	{ id: 'aircraft_code', label: 'Aircraft Code', classes: ['aircraftCodeCell'] },
	{ id: 'interval', label: 'Iridium Transmission Interval', classes: ['iridiumUpdateIntervalCell'], formatter: (v) => (v !== null ? `${v} sec` : "") },
	{ id: 'remarks', label: 'Remarks', classes: ['remarksCell'] },
	{ id: 'customer_name_operator', label: 'Operating Company', classes: ['operatingCompanyCell'] },
];

const formInputHash = {
	'interval': {
		key: 'interval',
		type: 'number',
		rules: {},
		default: ""
	},
	'remarks': {
		key:'remarks',
		//全角スペースは半角スペースに、スペースが連続の場合は半角スペース1つに置き換え
		compressSpace: true,
		rules: {
			required: false,
			//日本語入力可、スペース可、’“!@#$%^&*();:{}[]>?はNG
			pattern: /^[^'"!@#$%^&*();:{}[\]>?]{0,140}$/,
			maxLength: 140,
			maxByteLength: 140
		},
		default: ""
	}
};

const CoPilotListTable = memo(forwardRef(({handleClickAircraftCode, filterWord, handleEdit, setWaiting}, ref) => {
	const {
		coPilotList,
		updateCoPilotSetting,
		refreshCoPilotList
	} = useCoPilotList();

	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const list = useCallback(() => {
		if (filterWord === "") {
			return coPilotList;
		}
		return coPilotList.filter(row => {
			return columns.filter(col => ['sbd_imei', 'aircraft_code'].includes(col.id)).some(col => {
				const value = row[col.id] !== null ? row[col.id] : t('Not associated');
				return value.indexOf(filterWord) !== -1;
			});
		});
	}, [coPilotList, filterWord, t]);

	useEffect(() => {
		return () => {
			refreshCoPilotList();
		}
	}, [refreshCoPilotList]);

	useImperativeHandle(ref, () => ({
		update: (updateData) => {
			setWaiting(true);
			updateCoPilotSetting(updateData)
				.then(() => {
					console.log("update comp");
				})
				.catch(e => {
					console.error("update error", e);
					if (e?.response?.status === 428) {
						showWarning(t('validate_optimistic_lock'));
						refreshCoPilotList();
					} else {
						showError(`${t('Update failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		}
	}));

	return (
		<>
			{list().map((row) => {
				return (
					<TableRow hover role="checkbox" tabIndex={-1} key={row.sbd_imei}>
						<TableCell key='editBtn' className={clsx(classes.tableCell, classes.iconCell)} align="center">
							<RoleControl subject={SUBJ_COPI_SETTING_LIST} verb={VERB_UPDATE}>
								<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
									<Tooltip title="edit" arrow >
										<PencilIcon fontSize="small" />
									</Tooltip>
								</IconButton>
							</RoleControl>
						</TableCell>
						{columns.map((column, i) => {
							const classNames = ["tableCell"];
							if (column.classes) {
								classNames.push(...column.classes);
							}
							let value = row[column.id];
							if (column.formatter) {
								value = column.formatter(value);
							}
							return (
								<TableCell
									key={column.id}
									align={column.align}
									className={clsx(classNames.map(c=>classes[c]))}
								>
									{column.id !== 'aircraft_code' && value}
									{column.id === 'aircraft_code' && value &&
										<OnClickAnchor onClick={() => handleClickAircraftCode(row)}>{value}</OnClickAnchor>
									}
									{column.id === 'aircraft_code' && !value && t('Not associated')}
								</TableCell>
							);
						})}
					</TableRow>
				);
			})}
		</>
	);
}));

function CoPilotSettingList({changeAircraftSetting}) {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const [formData, setFormData] = useState({});
	const [filterWord, setFileterWord] = useRecoilState(coPilotListFilterWordState);
	const [open, setOpen] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const setAircraftListFileterWord = useSetRecoilState(aircraftListFilterWordState);
	const user = useRecoilValue(userState);

	const classes = useStyles();
	const {t} = useTranslation();

	const inputRefs = Object.keys(formInputHash).reduce((a, v) => (a[v] = createRef()) && a, {});

	const { control, getValues, reset, formState: { errors, isDirty, isValid } } = useForm({
		mode: 'onChange',
	});

	useEffect(() => {
		return () => {
			setFileterWord('');
		}
	}, [setFileterWord]);

	const makeTextRules = useCallback((key) => {
		const item = formInputHash[key];
		return {
			validate: {
				message: (v) => {
					const _v = !item.compressSpace ? v : v.replace(/　/g, ' ').replace(/\s+/g, " ");
					//必須チェック
					if (item.rules.required && (_v === undefined || _v === null || _v === "")) {
						return t('validate_required');
					}
					//文字数チェック
					if (item.rules.maxLength && _v.length > item.rules.maxLength) {
						return t('validate_max_length', {maxLength: item.rules.maxLength});
					}
					if (item.rules.maxByteLength && strSJISBytes(_v) > item.rules.maxByteLength) {
						return t('Too many characters');
					}
					//パターンチェック
					if (item.rules.pattern && !item.rules.pattern.test(_v)) {
						return t('validate_pattern');
					}
					return null;
				}
			}
		};
	}, [t]);

	const handleEdit = useCallback((row) => {
		const _formData = Object.assign({}, row);
		Object.keys(formInputHash).forEach(key => {
			if (formInputHash[key]) {
				if ('default' in formInputHash[key] && _formData[key] === null) {
					_formData[key] = formInputHash[key].default;
				}
			}
		});
		reset(_formData);
		setFormData(_formData);
		setOpen(true);
	}, [reset]);

	const handleSearch = useCallback(() => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}, [setFileterWord]);

	const handleDeleteFilter = useCallback(() => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}, [setFileterWord]);

	const handleSave = useCallback(() => {
		//入力された値で更新オブジェクトを作成
		const updateData = Object.keys(inputRefs).reduce((a, v) => {
			const item = formInputHash[v];
			const value = inputRefs[v].current.value;
			a[v] = !item.compressSpace ? value : value.replace(/　/g, ' ').replace(/\s+/g, " ");
			//空文字の場合はnullをセット
			if (a[v] === "") {
				a[v] = null;
			}
			return a;
		}, {});

		const data = {...formData, ...updateData, ...{'api_exec_user': user.userId}};

		childRef.current.update(data);
		setOpen(false);
	}, [formData, inputRefs, user.userId]);

	const handleClose = useCallback(() => {
		setOpen(false);
	}, []);

	const handleClickAircraftCode = useCallback((row) => {
		setAircraftListFileterWord(row ? row.aircraft_code : "");
		changeAircraftSetting();
	}, [changeAircraftSetting, setAircraftListFileterWord]);

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`SBD IMEI, ${t("Aircraft Code")}`}
						inputProps={{ 'aria-label': 'filter CoPilot List' }}
						inputRef={inputFilterRef}
						defaultValue={filterWord}
						// onChange={handleChange}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
			</Box>
			<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<TableContainer className={classes.container} ref={tableRef}>
						<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
							<TableHead>
								<TableRow className={classes.tableRow}>
									<TableCell
										key='edit'
										className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
									>
									</TableCell>
									{columns.map((column, i) => {
										const classNames = ["headerCell", "tableCell"];
										if (column.classes) {
											classNames.push(...column.classes);
										}
										return (
											<TableCell
												key={column.id}
												align={column.align}
												className={clsx(classNames.map(c=>classes[c]))}
											>
												{column.id !== 'aircraft_code' && t(column.label)}
												{column.id === 'aircraft_code' &&
													<OnClickAnchor onClick={() => handleClickAircraftCode()}>{t(column.label)}</OnClickAnchor>
												}
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								<CoPilotListTable handleClickAircraftCode={handleClickAircraftCode} filterWord={filterWord} ref={childRef} handleEdit={handleEdit} setWaiting={setWaiting} />
							</TableBody>
						</Table>
					</TableContainer>
				</Suspense>
			</Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '10px' }}>
							<Box style={{ marginLeft: 'auto'}}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={!(isDirty && isValid)}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
							<Box className={classes.dialogTitle}>{`${formData['sbd_imei']} (${formData['aircraft_code'] || t('Not associated')})`}</Box>
							<ul className={classes.dialogContent}>
								<li>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Iridium Transmission Interval')}</dt>
										<dd>
											<Controller
												key={'interval'}
												name={'interval'}
												control={control}
												render={({ field: { name, onChange, onBlur, value } }) => (
													<Select
														id={`${name}-select`}
														value={value}
														displayEmpty
														inputRef={inputRefs[name]}
														onChange={onChange}
														onBlur={onBlur}
														className={classes.intervalSecSelect}
														disabled={user.customer !== getValues().customer_code_payer}
													>
														<MenuItem value={5}>5 sec</MenuItem>
														<MenuItem value={10}>10 sec</MenuItem>
														<MenuItem value={20}>20 sec</MenuItem>
														<MenuItem value={30}>30 sec</MenuItem>
														<MenuItem value={60}>60 sec</MenuItem>
														<MenuItem value={120}>120 sec</MenuItem>
														<MenuItem value={300}>300 sec</MenuItem>
														<MenuItem value={600}>600 sec</MenuItem>
													</Select>
												)}
											/>
										</dd>
									</dl>
								</li>
								<li className={classes.note}>{t("Please be sure to turn on the FOSTER-CoPilot if changing 'Iridium Transmission Interval'.")}</li>
								<li className={classes.note}>{t("Intervals of 20 seconds or less are for testing purposes (e.g., EMI testing). Please contact Weathernews if you require a setting of 20 seconds or less.")}</li>
								<li>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Remarks')}</dt>
										<dd>
											<Controller
												key={'remarks'}
												control={control}
												rules={makeTextRules('remarks')}
												render={({ field: { name, onChange, onBlur, value } }) => (
													<TextField
														className={clsx(classes.formInputText, classes.remarksInputText)}
														name={name}
														id={name}
														variant="outlined"
														size="small"
														InputLabelProps={{
															shrink: true,
														}}
														error={!!errors[name]}
														inputRef={inputRefs[name]}
														// defaultValue={formData[name]}
														helperText={t(errors[name]?.message)}
														onChange={onChange}
														onBlur={onBlur}
														value={value}
													/>
												)}
												name={'remarks'}
											/>
										</dd>
									</dl>
								</li>
							</ul>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default CoPilotSettingList;
